import React, { useState, useEffect, useRef, useCallback } from 'react';
import { eventsAPI, contactsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  FileText,
  Plus,
  RefreshCw,
  Loader2,
  Check,
  MapPin,
  Phone,
  User,
  Clock,
  AlertTriangle,
  Car,
  Utensils,
  Shirt,
  Download,
  FileDown,
} from 'lucide-react';
import html2pdf from 'html2pdf.js';
import CallSheetPrintView from './CallSheetPrintView';

const DRESS_CODE_OPTIONS = [
  { value: 'Show Blacks', label: 'Show Blacks' },
  { value: 'Business Casual', label: 'Business Casual' },
  { value: 'Suits', label: 'Suits' },
  { value: 'Set/Strike Blacks', label: 'Set/Strike Blacks' },
  { value: 'Outdoors', label: 'Outdoors' },
];

const CallSheetsTab = ({ eventId, event }) => {
  const [callSheets, setCallSheets] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const printRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editedSheets, setEditedSheets] = useState({});
  const [savingSheets, setSavingSheets] = useState({}); // Track saving state per sheet
  const [savedSheets, setSavedSheets] = useState({}); // Track saved state per sheet
  const saveTimeoutsRef = useRef({}); // Track debounce timeouts per sheet

  useEffect(() => {
    loadData();
  }, [eventId]);

  const loadData = async () => {
    try {
      const [sheetsRes, contactsRes] = await Promise.all([
        eventsAPI.getCallSheets(eventId),
        contactsAPI.list({ limit: 1000 }),
      ]);
      setCallSheets(sheetsRes.data);
      // Handle paginated response format
      setContacts(contactsRes.data.data || contactsRes.data || []);
      
      // Initialize edited sheets state
      const edited = {};
      sheetsRes.data.forEach(sheet => {
        edited[sheet.id] = {
          warehouse_tech_id: sheet.warehouse_tech_id || '',
          parking_options: sheet.parking_options || '',
          crew_meeting_location: sheet.crew_meeting_location || '',
          dress_code: sheet.dress_code || '',
          meal_info: sheet.meal_info || '',
          notes: sheet.notes || '',
        };
      });
      setEditedSheets(edited);
    } catch (error) {
      console.error('Failed to load call sheets:', error);
      toast.error('Failed to load call sheets');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCallSheets = async () => {
    setGenerating(true);
    try {
      const response = await eventsAPI.generateCallSheets(eventId);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate call sheets');
    } finally {
      setGenerating(false);
    }
  };

  const handleFieldChange = (sheetId, field, value) => {
    const newEditedSheets = {
      ...editedSheets,
      [sheetId]: {
        ...editedSheets[sheetId],
        [field]: value,
      }
    };
    setEditedSheets(newEditedSheets);
    
    // Queue the change for hybrid save
    queueSheetSave(sheetId, newEditedSheets[sheetId]);
  };

  // Save with explicit data (avoids stale closure issues)
  const saveSheetWithData = useCallback(async (sheetId, data) => {
    // Clear any pending timeout
    if (saveTimeoutsRef.current[sheetId]) {
      clearTimeout(saveTimeoutsRef.current[sheetId]);
    }
    
    if (!data) return;
    
    setSavingSheets(prev => ({ ...prev, [sheetId]: true }));
    try {
      const response = await eventsAPI.updateCallSheet(eventId, sheetId, {
        warehouse_tech_id: data.warehouse_tech_id || '',
        parking_options: data.parking_options || '',
        crew_meeting_location: data.crew_meeting_location || '',
        dress_code: data.dress_code || '',
        meal_info: data.meal_info || '',
        notes: data.notes || '',
      });
      // Update local callSheets with resolved data from backend
      if (response?.data) {
        setCallSheets(prev => prev.map(s => s.id === sheetId ? response.data : s));
      }
      setSavedSheets(prev => ({ ...prev, [sheetId]: true }));
      setTimeout(() => {
        setSavedSheets(prev => ({ ...prev, [sheetId]: false }));
      }, 2000);
    } catch (error) {
      console.error('Failed to save call sheet:', error);
      toast.error('Failed to save call sheet');
    } finally {
      setSavingSheets(prev => ({ ...prev, [sheetId]: false }));
    }
  }, [eventId]);

  // Save immediately (for blur events) - reads latest state via ref
  const saveSheetNow = useCallback(async (sheetId) => {
    const data = editedSheets[sheetId];
    if (!data) return;
    await saveSheetWithData(sheetId, data);
  }, [editedSheets, saveSheetWithData]);

  // Immediate save for dropdown changes - passes data directly to avoid stale closure
  const saveFieldImmediately = useCallback((sheetId, field, value) => {
    const currentData = editedSheets[sheetId] || {};
    const updatedData = { ...currentData, [field]: value };
    setEditedSheets(prev => ({ ...prev, [sheetId]: updatedData }));
    // Clear any pending queued save
    if (saveTimeoutsRef.current[sheetId]) {
      clearTimeout(saveTimeoutsRef.current[sheetId]);
    }
    saveSheetWithData(sheetId, updatedData);
  }, [editedSheets, saveSheetWithData]);

  // Queue a change - hybrid save with backup timer (1 second of inactivity)
  const queueSheetSave = useCallback((sheetId, data) => {
    // Clear existing timeout for this sheet
    if (saveTimeoutsRef.current[sheetId]) {
      clearTimeout(saveTimeoutsRef.current[sheetId]);
    }

    // Set backup timeout (1 second of inactivity)
    saveTimeoutsRef.current[sheetId] = setTimeout(async () => {
      await saveSheetWithData(sheetId, data);
    }, 1000);
  }, [saveSheetWithData]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(saveTimeoutsRef.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  const exportSinglePDF = async (sheet) => {
    setExportingId(sheet.id);
    try {
      const container = document.createElement('div');
      container.innerHTML = generatePrintHTML(sheet);
      document.body.appendChild(container);

      const opt = {
        margin: [0.4, 0.4, 0.4, 0.4],
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css'], avoid: ['.cs-section', '.cs-row', '.cs-schedule-row', 'tr'] }
      };

      const worker = html2pdf().set(opt).from(container).toPdf();
      const pdfObj = await worker.get('pdf');
      const pdfBlob = pdfObj.output('blob');
      document.body.removeChild(container);
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CallSheet_${event?.event_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Event'}_${sheet.date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Call sheet exported');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export call sheet');
    } finally {
      setExportingId(null);
    }
  };

  const exportAllPDF = async () => {
    if (callSheets.length === 0) {
      toast.error('No call sheets to export');
      return;
    }

    setExporting(true);
    try {
      const container = document.createElement('div');
      
      callSheets.forEach((sheet, index) => {
        const sheetHTML = generatePrintHTML(sheet, index < callSheets.length - 1);
        container.innerHTML += sheetHTML;
      });
      
      document.body.appendChild(container);

      const opt = {
        margin: [0.4, 0.4, 0.4, 0.4],
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css'], avoid: ['.cs-section', '.cs-row', '.cs-schedule-row', 'tr'] }
      };

      const worker = html2pdf().set(opt).from(container).toPdf();
      const pdfObj = await worker.get('pdf');
      const pdfBlob = pdfObj.output('blob');
      document.body.removeChild(container);
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CallSheets_${event?.event_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Event'}_All.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Exported ${callSheets.length} call sheets`);
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export call sheets');
    } finally {
      setExporting(false);
    }
  };

  const generatePrintHTML = (sheet, addPageBreak = false) => {
    const formatDateStr = (dateStr) => {
      if (!dateStr) return '';
      try {
        return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy');
      } catch {
        return dateStr;
      }
    };

    const scheduleRows = sheet.schedule_items?.map(item => `
      <tr class="cs-schedule-row" style="border-bottom: 1px solid #eee; page-break-inside: avoid;">
        <td style="padding: 8px; font-family: monospace; font-weight: bold; vertical-align: top;">${formatTime12h(item.time)}</td>
        <td style="padding: 8px;">
          <div>${item.action || '-'}</div>
          ${item.notes ? `<div style="font-size: 10px; color: #666; font-style: italic; margin-top: 4px;">${item.notes}</div>` : ''}
        </td>
      </tr>
    `).join('') || '';

    return `
      <div style="width: 7.7in; padding: 0.1in; font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; box-sizing: border-box; background: white; color: black; ${addPageBreak ? 'page-break-after: always;' : ''}">
        <!-- AV Factory Logo/Header -->
        <div class="cs-section" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #000; page-break-inside: avoid;">
          <div style="display: flex; align-items: center;">
            <img src="/avf-logo.jpg" alt="AV Factory" style="height: 50px; width: auto;" />
          </div>
          <div style="text-align: right; color: #666; font-size: 10px;">
            <p style="margin: 0;">www.avfactory.com</p>
          </div>
        </div>
        <!-- Header -->
        <div class="cs-section" style="border-bottom: 3px solid #000; padding-bottom: 12px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: flex-start; page-break-inside: avoid;">
          <div>
            <h1 style="font-size: 24px; font-weight: bold; margin: 0;">CREW CALL SHEET</h1>
            <p style="font-size: 14px; color: #666; margin: 4px 0 0 0;">${formatDateStr(sheet.date)}</p>
          </div>
          ${sheet.flex_number ? `
            <div style="text-align: right;">
              <p style="font-size: 12px; color: #666; margin: 0;">Flex #</p>
              <p style="font-size: 18px; font-weight: bold; margin: 0;">${sheet.flex_number}</p>
            </div>
          ` : ''}
        </div>

        <!-- Event Name -->
        <div class="cs-section" style="margin-bottom: 16px; page-break-inside: avoid;">
          <h2 style="font-size: 20px; font-weight: bold; margin: 0;">${sheet.event_name || ''}</h2>
        </div>

        <!-- Two Column Layout -->
        <div class="cs-section" style="display: flex; gap: 24px; margin-bottom: 16px; page-break-inside: avoid;">
          <!-- Left Column -->
          <div style="flex: 1;">
            <div class="cs-row" style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin-bottom: 12px; page-break-inside: avoid;">
              <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Location</p>
              <p style="font-weight: bold; margin: 0; font-size: 14px;">${sheet.location_name || 'TBD'}</p>
              ${sheet.location_address ? `<p style="margin: 4px 0 0 0; font-size: 12px;">${sheet.location_address}${sheet.location_city ? `, ${sheet.location_city}` : ''}${sheet.location_state ? ` ${sheet.location_state}` : ''}${sheet.location_zip ? ` ${sheet.location_zip}` : ''}</p>` : ''}
              ${sheet.location_phone ? `<p style="margin: 4px 0 0 0; font-size: 12px;">Tel: ${sheet.location_phone}</p>` : ''}
            </div>
            ${sheet.room ? `
              <div class="cs-row" style="background: #f5f5f5; padding: 12px; border-radius: 4px; page-break-inside: avoid;">
                <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Room / Space</p>
                <p style="font-weight: bold; margin: 0;">${sheet.room}</p>
              </div>
            ` : ''}
          </div>

          <!-- Right Column - Contacts -->
          <div style="flex: 1;">
            <div class="cs-row" style="background: #e8f5e9; padding: 12px; border-radius: 4px; margin-bottom: 8px; page-break-inside: avoid;">
              <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Account Executive</p>
              <p style="font-weight: bold; margin: 0;">${sheet.account_exec_name || 'Not assigned'}</p>
              ${sheet.account_exec_phone ? `<p style="margin: 2px 0 0 0; font-size: 12px;">Tel: ${sheet.account_exec_phone}</p>` : ''}
            </div>
            <div class="cs-row" style="background: #e3f2fd; padding: 12px; border-radius: 4px; margin-bottom: 8px; page-break-inside: avoid;">
              <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Project Manager</p>
              <p style="font-weight: bold; margin: 0;">${sheet.project_manager_name || 'Not assigned'}</p>
              ${sheet.project_manager_phone ? `<p style="margin: 2px 0 0 0; font-size: 12px;">Tel: ${sheet.project_manager_phone}</p>` : ''}
            </div>
            <div class="cs-row" style="background: #fff3e0; padding: 12px; border-radius: 4px; page-break-inside: avoid;">
              <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">On-Call Warehouse Tech</p>
              <p style="font-weight: bold; margin: 0;">${sheet.warehouse_tech_name || 'Not assigned'}</p>
              ${sheet.warehouse_tech_phone ? `<p style="margin: 2px 0 0 0; font-size: 12px;">Tel: ${sheet.warehouse_tech_phone}</p>` : ''}
            </div>
          </div>
        </div>

        <!-- Crew Details Row -->
        <div class="cs-section" style="display: flex; gap: 16px; margin-bottom: 16px; page-break-inside: avoid;">
          ${sheet.crew_meeting_location ? `
            <div class="cs-row" style="flex: 1; background: #f5f5f5; padding: 12px; border-radius: 4px; page-break-inside: avoid;">
              <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Crew Meeting Location</p>
              <p style="font-weight: bold; margin: 0;">${sheet.crew_meeting_location}</p>
            </div>
          ` : ''}
          ${sheet.dress_code ? `
            <div class="cs-row" style="flex: 1; background: #f3e5f5; padding: 12px; border-radius: 4px; page-break-inside: avoid;">
              <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Dress Code</p>
              <p style="font-weight: bold; margin: 0;">${sheet.dress_code}</p>
            </div>
          ` : ''}
        </div>

        <!-- Meal Info -->
        ${sheet.meal_info ? `
          <div class="cs-section" style="background: #fff8e1; padding: 12px; border-radius: 4px; margin-bottom: 16px; page-break-inside: avoid;">
            <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Meal Information</p>
            <p style="margin: 0; white-space: pre-wrap;">${sheet.meal_info}</p>
          </div>
        ` : ''}

        <!-- Schedule -->
        ${sheet.schedule_items?.length > 0 ? `
          <div class="cs-section" style="margin-bottom: 16px;">
            <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 8px 0; border-bottom: 1px solid #ddd; padding-bottom: 4px; page-break-after: avoid;">Schedule</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background: #f5f5f5;">
                  <th style="text-align: left; padding: 8px; font-size: 11px; font-weight: bold; width: 80px;">Time</th>
                  <th style="text-align: left; padding: 8px; font-size: 11px; font-weight: bold;">Activity</th>
                </tr>
              </thead>
              <tbody>${scheduleRows}</tbody>
            </table>
          </div>
        ` : ''}

        <!-- Notes -->
        ${sheet.notes ? `
          <div class="cs-section" style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin-bottom: 16px; page-break-inside: avoid;">
            <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Notes</p>
            <p style="margin: 0; white-space: pre-wrap;">${sheet.notes}</p>
          </div>
        ` : ''}

        <!-- Bottom Section -->
        <div class="cs-section" style="display: flex; gap: 16px; page-break-inside: avoid;">
          <div class="cs-row" style="flex: 1; background: #f5f5f5; padding: 12px; border-radius: 4px; page-break-inside: avoid;">
            <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Parking</p>
            ${sheet.parking_options ? `<p style="margin: 0; font-size: 11px;">${sheet.parking_options}</p>` : `<p style="margin: 0; font-size: 11px; color: #999;">Not specified</p>`}
            <p style="margin: 8px 0 0 0; font-size: 10px; font-style: italic; color: #666;">* Vehicles are parked at owners risk. Parking is not reimbursed unless noted.</p>
          </div>
          <div class="cs-row" style="flex: 1; background: #ffebee; padding: 12px; border-radius: 4px; border: 1px solid #ffcdd2; page-break-inside: avoid;">
            <p style="font-size: 10px; color: #c62828; text-transform: uppercase; margin: 0 0 4px 0; font-weight: bold;">Nearest Emergency Room</p>
            ${sheet.nearest_er_name ? `
              <p style="font-weight: bold; margin: 0;">${sheet.nearest_er_name}</p>
              ${sheet.nearest_er_address ? `<p style="margin: 2px 0 0 0; font-size: 11px;">${sheet.nearest_er_address}</p>` : ''}
              ${sheet.nearest_er_phone ? `<p style="margin: 2px 0 0 0; font-size: 12px; font-weight: bold;">Tel: ${sheet.nearest_er_phone}</p>` : ''}
            ` : `<p style="margin: 0; font-size: 11px; color: #999;">Not specified</p>`}
          </div>
        </div>
      </div>
    `;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatTime12h = (time) => {
    if (!time) return '--:--';
    const [h, m] = time.split(':');
    const hi = parseInt(h, 10);
    if (isNaN(hi)) return time;
    const ampm = hi >= 12 ? 'PM' : 'AM';
    const h12 = hi === 0 ? 12 : hi > 12 ? hi - 12 : hi;
    return `${h12}:${m || '00'} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5 text-amber-400" />
          Call Sheets
        </h3>
        <div className="flex items-center gap-2">
          {callSheets.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={exportAllPDF}
              disabled={exporting}
              data-testid="export-all-callsheets-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4 mr-2" />
                  Export All PDF
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateCallSheets}
            disabled={generating || !event?.start_date}
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Generate Call Sheets
              </>
            )}
          </Button>
        </div>
      </div>

      {!event?.start_date && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          <p className="text-sm text-yellow-400">
            Set event start and end dates to generate call sheets for each day.
          </p>
        </div>
      )}

      {callSheets.length === 0 ? (
        <Card className="border-border/50 bg-card/50">
          <CardContent className="py-12">
            <p className="text-muted-foreground text-center">
              No call sheets yet. Click "Generate Call Sheets" to create one for each day of the event.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="single" collapsible className="space-y-4">
          {callSheets.map((sheet) => (
            <AccordionItem
              key={sheet.id}
              value={sheet.id}
              className="border border-border/50 rounded-lg bg-card/50 overflow-hidden"
            >
              <AccordionTrigger className="px-4 py-3 hover:bg-muted/30 [&[data-state=open]]:bg-muted/30">
                <div className="flex items-center gap-3 flex-1">
                  <Badge variant="outline" className="badge-event">
                    {formatDate(sheet.date)}
                  </Badge>
                  {editedSheets[sheet.id]?.dress_code && (
                    <Badge variant="outline" className="text-xs">
                      {editedSheets[sheet.id].dress_code}
                    </Badge>
                  )}
                  {/* Auto-save indicator */}
                  <div className="ml-auto mr-4">
                    {savingSheets[sheet.id] && (
                      <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Saving...
                      </Badge>
                    )}
                    {savedSheets[sheet.id] && !savingSheets[sheet.id] && (
                      <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                        <Check className="h-3 w-3 mr-1" />
                        Saved
                      </Badge>
                    )}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-6 pt-4">
                  {/* Read-only Event Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Event</p>
                      <p className="font-medium">{sheet.event_name}</p>
                      {sheet.flex_number && (
                        <p className="text-sm text-muted-foreground">Flex #{sheet.flex_number}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Location</p>
                      <p className="font-medium">{sheet.location_name || 'Not set'}</p>
                      {sheet.location_address && (
                        <p className="text-sm text-muted-foreground">
                          {sheet.location_address}
                          {sheet.location_city && `, ${sheet.location_city}`}
                          {sheet.location_state && ` ${sheet.location_state}`}
                          {sheet.location_zip && ` ${sheet.location_zip}`}
                        </p>
                      )}
                      {sheet.location_phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" />
                          {sheet.location_phone}
                        </p>
                      )}
                    </div>
                    {sheet.room && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Room</p>
                        <p className="font-medium">{sheet.room}</p>
                      </div>
                    )}
                  </div>

                  {/* Key Contacts */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Account Exec */}
                    <div className="p-3 bg-muted/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-4 w-4 text-emerald-400" />
                        <p className="text-xs text-muted-foreground uppercase">Account Executive</p>
                      </div>
                      <p className="font-medium">{sheet.account_exec_name || 'Not set'}</p>
                      {sheet.account_exec_phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {sheet.account_exec_phone}
                        </p>
                      )}
                    </div>

                    {/* Project Manager */}
                    <div className="p-3 bg-muted/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-4 w-4 text-cyan-400" />
                        <p className="text-xs text-muted-foreground uppercase">Project Manager</p>
                      </div>
                      <p className="font-medium">{sheet.project_manager_name || 'Not set'}</p>
                      {sheet.project_manager_phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {sheet.project_manager_phone}
                        </p>
                      )}
                    </div>

                    {/* Warehouse Tech - Editable */}
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-4 w-4 text-amber-400" />
                        <p className="text-xs text-muted-foreground uppercase">On-Call Warehouse Tech</p>
                      </div>
                      <Select
                        value={editedSheets[sheet.id]?.warehouse_tech_id || 'none'}
                        onValueChange={(value) => {
                          saveFieldImmediately(sheet.id, 'warehouse_tech_id', value === 'none' ? '' : value);
                        }}
                      >
                        <SelectTrigger className="bg-background/50 h-8 text-sm">
                          <SelectValue placeholder="Select technician" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not assigned</SelectItem>
                          {contacts.filter(c => c.role_tags && c.role_tags.includes('AVF Staff')).map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.contact_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Venue Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Parking */}
                    <div className="p-3 bg-muted/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Car className="h-4 w-4 text-amber-400" />
                        <p className="text-xs text-muted-foreground uppercase">Parking</p>
                      </div>
                      <Textarea
                        value={editedSheets[sheet.id]?.parking_options || ''}
                        onChange={(e) => handleFieldChange(sheet.id, 'parking_options', e.target.value)}
                        onBlur={() => saveSheetNow(sheet.id)}
                        placeholder="Parking instructions (pre-filled from venue)"
                        rows={2}
                        className="bg-background/50 text-sm"
                        data-testid={`parking-options-${sheet.id}`}
                      />
                      <p className="text-xs text-muted-foreground mt-2 italic">
                        Vehicles are parked at owners risk. Parking is not reimbursed unless noted.
                      </p>
                    </div>

                    {/* Emergency Room */}
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-red-400" />
                        <p className="text-xs text-muted-foreground uppercase">Nearest Emergency Room</p>
                      </div>
                      {sheet.nearest_er_name ? (
                        <>
                          <p className="font-medium">{sheet.nearest_er_name}</p>
                          {sheet.nearest_er_address && (
                            <p className="text-sm text-muted-foreground">{sheet.nearest_er_address}</p>
                          )}
                          {sheet.nearest_er_phone && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {sheet.nearest_er_phone}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">Not specified in location</p>
                      )}
                    </div>
                  </div>

                  {/* Editable Fields */}
                  <div className="space-y-4 border-t border-border/50 pt-4">
                    <p className="text-sm font-medium text-muted-foreground">Call Sheet Details</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-amber-400" />
                          Crew Meeting Location
                        </Label>
                        <Input
                          value={editedSheets[sheet.id]?.crew_meeting_location || ''}
                          onChange={(e) => handleFieldChange(sheet.id, 'crew_meeting_location', e.target.value)}
                          onBlur={() => saveSheetNow(sheet.id)}
                          placeholder="Where should crew meet?"
                          className="bg-background/50"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Shirt className="h-4 w-4 text-cyan-400" />
                          Dress Code
                        </Label>
                        <Select
                          value={editedSheets[sheet.id]?.dress_code || 'none'}
                          onValueChange={(value) => {
                            saveFieldImmediately(sheet.id, 'dress_code', value === 'none' ? '' : value);
                          }}
                        >
                          <SelectTrigger className="bg-background/50">
                            <SelectValue placeholder="Select dress code" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Not specified</SelectItem>
                            {DRESS_CODE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Utensils className="h-4 w-4 text-orange-400" />
                        Meal Information
                      </Label>
                      <Textarea
                        value={editedSheets[sheet.id]?.meal_info || ''}
                        onChange={(e) => handleFieldChange(sheet.id, 'meal_info', e.target.value)}
                        onBlur={() => saveSheetNow(sheet.id)}
                        placeholder="Meal times, locations, dietary notes..."
                        rows={2}
                        className="bg-background/50"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Call Sheet Notes</Label>
                      <Textarea
                        value={editedSheets[sheet.id]?.notes || ''}
                        onChange={(e) => handleFieldChange(sheet.id, 'notes', e.target.value)}
                        onBlur={() => saveSheetNow(sheet.id)}
                        placeholder="Additional information for crew..."
                        rows={3}
                        className="bg-background/50"
                      />
                    </div>
                  </div>

                  {/* Schedule for this day */}
                  {sheet.schedule_items?.length > 0 && (
                    <div className="border-t border-border/50 pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="h-4 w-4 text-cyan-400" />
                        <p className="text-sm font-medium">Schedule</p>
                      </div>
                      <div className="space-y-2">
                        {sheet.schedule_items.map((item, idx) => (
                          <div key={idx} className="p-2 bg-muted/20 rounded">
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-sm text-cyan-400 w-20">
                                {formatTime12h(item.time)}
                              </span>
                              <span className="text-sm">{item.action || 'No action'}</span>
                            </div>
                            {item.notes && (
                              <p className="text-xs text-muted-foreground mt-1 ml-[76px] italic">
                                {item.notes}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Export PDF Button */}
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => exportSinglePDF(sheet)}
                      disabled={exportingId === sheet.id}
                      data-testid={`export-callsheet-${sheet.id}-btn`}
                    >
                      {exportingId === sheet.id ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Export PDF
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};

export default CallSheetsTab;
